import { useMemo } from "react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { UserCheck, Stethoscope, PhoneCall } from "lucide-react";

const CLEAR_VALUE = "__none__";

function RoleSelect({
  testId,
  icon: Icon,
  accent,
  label,
  hint,
  value,
  options,
  onChange,
  children,
}) {
  return (
    <div
      className="p-4 rounded-lg border border-[#e2e8f0] bg-white hover:bg-[#f8fafc] transition-colors"
      data-testid={`role-card-${testId}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center"
          style={{ backgroundColor: `${accent}18`, color: accent }}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <div className="font-heading font-semibold text-sm text-[#0f172a]">{label}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#475569]">{hint}</div>
        </div>
      </div>
      <Select
        value={value || CLEAR_VALUE}
        onValueChange={(v) => onChange(v === CLEAR_VALUE ? "" : v)}
      >
        <SelectTrigger
          className="border-[#e2e8f0] bg-white focus:ring-1"
          style={{ boxShadow: value ? `inset 0 0 0 1px ${accent}` : undefined }}
          data-testid={`role-select-${testId}`}
        >
          <SelectValue placeholder="Not assigned" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={CLEAR_VALUE}>
            <span className="text-[#475569] italic">Not assigned</span>
          </SelectItem>
          {options.map((o) => (
            <SelectItem key={o.id} value={o.id} data-testid={`role-option-${testId}-${o.id}`}>
              {o.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {children}
    </div>
  );
}

export default function DailyRolesPanel({
  providers,
  medRounderId,
  medRounderPhp,
  psychOnCallId,
  medOnCallId,
  onSetMedRounder,
  onToggleMedRounderPhp,
  onSetPsychOnCall,
  onSetMedOnCall,
}) {
  const medicalRounders = useMemo(
    () => providers.filter((p) => (p.roles || []).includes("medical_rounder")),
    [providers]
  );
  const allProviders = providers;

  return (
    <div
      className="bg-white border border-[#e2e8f0] rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
      data-testid="daily-roles-panel"
    >
      <div className="mb-4">
        <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
          2. Daily role coverage
        </p>
        <h3 className="font-heading text-lg font-medium mt-1">
          Medical rounder & on-call assignments
        </h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <RoleSelect
          testId="medical-rounder"
          icon={Stethoscope}
          accent="#0284c7"
          label="Medical Rounder"
          hint="H&P · Follow-up · PHP intake"
          value={medRounderId}
          options={medicalRounders}
          onChange={onSetMedRounder}
        >
          {medRounderId && (
            <label
              className="mt-3 flex items-center gap-2 text-xs text-[#0f172a] cursor-pointer select-none"
              data-testid="med-rounder-php-label"
            >
              <input
                type="checkbox"
                checked={!!medRounderPhp}
                onChange={onToggleMedRounderPhp}
                data-testid="med-rounder-php-toggle"
                className="w-4 h-4 rounded border-[#c4c4c0] accent-[#d96c4e]"
              />
              <span>Also covers <span className="font-semibold text-[#d96c4e]">PHP medical intake</span></span>
            </label>
          )}
          {medicalRounders.length === 0 && (
            <p className="mt-2 text-[11px] text-[#475569] italic">
              No provider tagged as Medical Rounder yet. Add one from the Providers panel.
            </p>
          )}
        </RoleSelect>

        <RoleSelect
          testId="psych-on-call"
          icon={PhoneCall}
          accent="#6b4f8e"
          label="Psych On Call"
          hint="No patient assignments"
          value={psychOnCallId}
          options={allProviders}
          onChange={onSetPsychOnCall}
        />

        <RoleSelect
          testId="medical-on-call"
          icon={UserCheck}
          accent="#c2593e"
          label="Medical On Call"
          hint="No patient assignments"
          value={medOnCallId}
          options={allProviders}
          onChange={onSetMedOnCall}
        />
      </div>
    </div>
  );
}
